import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Check, X, Clock, FileText, Car, User, BookOpenText, Download, Satellite, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { fmtDate, fmtDateTime as fmt } from "../lib/format";
import { AssignDialog } from "../components/AssignDialog";

const BSTATUS = {
  menunggu: { label: "Menunggu", cls: "bg-slate-500 hover:bg-slate-500" },
  menunggu_kelulusan: { label: "Menunggu Kelulusan", cls: "bg-amber-500 hover:bg-amber-500" },
  diluluskan: { label: "Diluluskan", cls: "bg-[#198754] hover:bg-[#198754]" },
  ditolak: { label: "Ditolak", cls: "bg-[#DC3545] hover:bg-[#DC3545]" },
  selesai: { label: "Selesai", cls: "bg-[#10056E] hover:bg-[#10056E]" },
};

function Step({ done, danger, icon: Icon, title, sub, last }) {
  const circle = danger
    ? "bg-red-100 text-[#DC3545] border-red-200"
    : done
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : "bg-slate-100 text-slate-400 border-slate-200";
  return (
    <div className="flex-1 flex items-start gap-3">
      <div className="flex flex-col items-center">
        <div className={`h-10 w-10 rounded-full border flex items-center justify-center shrink-0 ${circle}`}>
          <Icon className="h-4.5 w-4.5" />
        </div>
        {!last && <div className={`w-px flex-1 min-h-6 ${done ? "bg-emerald-300" : "bg-slate-200"}`} />}
      </div>
      <div className="pb-6">
        <p className={`text-sm font-semibold ${done || danger ? "" : "text-slate-400"}`}>{title}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [rejectMode, setRejectMode] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const canProcess = user?.role === "admin" || user?.role === "operator";

  const load = useCallback(() => {
    api.get(`/bookings/${id}`).then((r) => setData(r.data)).catch((e) => setError(apiError(e) || "Tempahan tidak dijumpai"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const finalDecide = async (action) => {
    if (action === "tolak" && !remarks.trim()) {
      setRejectMode(true);
      return;
    }
    setBusy(true);
    try {
      await api.put(`/bookings/${id}/final`, { action, remarks: remarks || null });
      toast.success(action === "lulus" ? "Kelulusan akhir diberikan — staf dimaklumkan" : "Tempahan ditolak");
      setRejectMode(false);
      setRemarks("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (error) return (
    <div className="max-w-md mx-auto mt-12 border border-red-200 bg-red-50 rounded-xl p-6 text-center" data-testid="booking-error-card">
      <p className="text-sm font-semibold text-[#DC3545]">{error}</p>
      <p className="text-xs text-muted-foreground mt-1">Sila kembali ke senarai tempahan.</p>
    </div>
  );
  if (!data) return <p className="text-sm text-muted-foreground">Memuatkan...</p>;

  const { booking: b, vehicle: v, trip, telematics: tm, driver_trips: driverTrips } = data;
  const rejected = b.status === "ditolak";
  const assigned = !!b.vehicle_id;
  const approved = ["diluluskan", "selesai"].includes(b.status);

  const doReject = async () => {
    if (!remarks.trim()) { toast.error("Sila nyatakan sebab penolakan"); return; }
    setBusy(true);
    try {
      await api.put(`/bookings/${id}/reject`, { remarks });
      toast.success("Tempahan ditolak");
      setRejectOpen(false);
      setRemarks("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!window.confirm("Padam tempahan ini secara kekal? Log perjalanan berkaitan juga akan dipadam.")) return;
    setBusy(true);
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Tempahan dipadam");
      navigate("/tempahan");
    } catch (err) {
      toast.error(apiError(err));
      setBusy(false);
    }
  };

  const downloadReport = async () => {
    const res = await api.get(`/bookings/${id}/report`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `laporan_tempahan_${id.slice(0, 8)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 fade-up" data-testid="booking-detail-page">
      <div className="flex flex-wrap items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate("/tempahan")} data-testid="back-to-bookings-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Kembali
        </Button>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">Tempahan — {b.destination}</h1>
            <Badge className={BSTATUS[b.status]?.cls} data-testid="booking-detail-status">{BSTATUS[b.status]?.label}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">ID: {b.id}</p>
        </div>
        <Button size="sm" className="ml-auto bg-[#10056E] hover:bg-[#2a17a8]" onClick={downloadReport} data-testid="booking-report-btn">
          <Download className="h-4 w-4 mr-2" /> Muat Turun Laporan
        </Button>
      </div>

      {/* Operator/Admin assignment action */}
      {canProcess && b.status === "menunggu" && (
        <Card className="border-[#10056E]/30 bg-indigo-50/40 rounded-xl" data-testid="assign-action-panel">
          <CardContent className="p-4 sm:p-5 space-y-3">
            <div className="flex items-start gap-2.5">
              <Car className="h-5 w-5 text-[#10056E] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-[#10056E]">Tindakan Diperlukan — Tugasan Kenderaan</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  Permohonan <b>{b.driver_name}</b> ke <b>{b.destination}</b> sedang menunggu tugasan kenderaan.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button className="flex-1 h-11 bg-[#198754] hover:bg-[#157347]" disabled={busy}
                onClick={() => setAssignOpen(true)} data-testid="detail-assign-btn">
                <Check className="h-4 w-4 mr-2" /> Tugaskan Kenderaan & Pemandu
              </Button>
              <Button variant="outline" className="flex-1 h-11 text-[#DC3545] border-[#DC3545] hover:bg-red-50 bg-white" disabled={busy}
                onClick={() => { setRemarks(""); setRejectOpen(true); }} data-testid="detail-reject-btn">
                <X className="h-4 w-4 mr-2" /> Tolak Tempahan
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin final approval action */}
      {user?.role === "admin" && b.status === "menunggu_kelulusan" && (
        <Card className="border-amber-300 bg-amber-50 rounded-xl" data-testid="final-approval-panel">
          <CardContent className="p-4 sm:p-5 space-y-3">
            <div className="flex items-start gap-2.5">
              <ShieldCheck className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-amber-900">Kelulusan Akhir Diperlukan</p>
                <p className="text-xs text-amber-800 mt-0.5">
                  {b.assigned_by} telah menugaskan kenderaan <b>{b.vehicle_reg}</b>{b.assigned_pemandu_name ? <> dengan pemandu <b>{b.assigned_pemandu_name}</b></> : null}. Sila beri keputusan akhir anda.
                </p>
              </div>
            </div>
            {rejectMode && (
              <div className="space-y-1.5">
                <Label className="text-amber-900">Sebab Penolakan</Label>
                <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} className="bg-white"
                  placeholder="Nyatakan sebab..." data-testid="final-reject-remarks" />
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button className="flex-1 h-11 bg-[#198754] hover:bg-[#157347]" disabled={busy}
                onClick={() => finalDecide("lulus")} data-testid="detail-final-approve-btn">
                <Check className="h-4 w-4 mr-2" /> Luluskan Tempahan
              </Button>
              <Button variant="outline" className="flex-1 h-11 text-[#DC3545] border-[#DC3545] hover:bg-red-50 bg-white" disabled={busy}
                onClick={() => finalDecide("tolak")} data-testid="detail-final-reject-btn">
                <X className="h-4 w-4 mr-2" /> {rejectMode ? "Sahkan Penolakan" : "Tolak"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm rounded-xl">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Status Permohonan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col">
              <Step done icon={FileText} title="Permohonan Dihantar" sub={fmt(b.created_at)} />
              <Step
                done={assigned}
                danger={rejected && !assigned}
                icon={rejected && !assigned ? X : Car}
                title={rejected && !assigned ? "Ditolak" : assigned ? "Kenderaan Ditugaskan" : "Tugasan Kenderaan"}
                sub={assigned ? `${b.vehicle_reg} oleh ${b.assigned_by || b.processed_by || "-"}` : rejected ? `oleh ${b.processed_by || "-"}` : "Sedang disemak oleh pengendali"}
              />
              <Step
                done={approved}
                danger={rejected && assigned}
                icon={rejected && assigned ? X : Check}
                title={rejected && assigned ? "Ditolak" : approved ? "Kelulusan Akhir Diberikan" : "Kelulusan Akhir Admin"}
                sub={approved ? `oleh ${b.approved_by || b.processed_by || "-"}${b.approved_at || b.processed_at ? " · " + fmt(b.approved_at || b.processed_at) : ""}` : rejected && assigned ? `oleh ${b.processed_by || "-"}` : b.status === "menunggu_kelulusan" ? "Menunggu keputusan pentadbir" : "-"}
              />
              <Step
                done={!!trip}
                icon={BookOpenText}
                title={trip ? "Perjalanan Dilog" : "Log Perjalanan"}
                sub={trip ? `${trip.distance_km} km · ${trip.kmpl ?? "-"} Km/L` : rejected ? "Tidak berkenaan" : "Menunggu rekod staf"}
                last
              />
            </div>
            {b.remarks && (
              <div className="mt-2 bg-slate-50 border rounded-lg p-3 text-sm">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Catatan Pengendali</p>
                {b.remarks}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm rounded-xl">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2"><Clock className="h-4 w-4" /> Butiran Tempahan</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3 text-sm">
              {[
                ["Mula", fmt(b.start_datetime)],
                ["Tamat", fmt(b.end_datetime)],
                ["Destinasi", b.destination],
                ["Tujuan", b.purpose],
                ["Anggaran Jarak", `${b.estimated_distance} km`],
                ["Bilangan Penumpang", b.passengers || 1],
                ["Dimohon Pada", fmt(b.created_at)],
              ].map(([k, val]) => (
                <div key={k} className="flex justify-between gap-4 border-b last:border-0 pb-2 last:pb-0">
                  <dt className="text-muted-foreground shrink-0">{k}</dt>
                  <dd className="font-semibold text-right">{val}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-slate-200 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2"><User className="h-4 w-4" /> Staf</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              <p className="font-semibold">{b.driver_name}</p>
              <p className="text-muted-foreground">{b.driver_email}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2"><Car className="h-4 w-4" /> Kenderaan Ditugaskan</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {v ? (
                <div className="space-y-1">
                  <p className="font-bold text-lg">{v.registration_number}</p>
                  <p className="text-muted-foreground">{v.make} {v.model} · {v.vehicle_type} · {v.fuel_type}</p>
                  <p className="text-muted-foreground">Odometer: {Number(v.current_odometer).toLocaleString()} km</p>
                </div>
              ) : (
                <p className="text-muted-foreground">Belum ada kenderaan ditugaskan.</p>
              )}
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm rounded-xl">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2"><User className="h-4 w-4" /> Pemandu Ditugaskan</CardTitle>
            </CardHeader>
            <CardContent className="text-sm" data-testid="assigned-pemandu-card">
              {b.assigned_pemandu_name ? (
                <p className="font-semibold">{b.assigned_pemandu_name}</p>
              ) : (
                <p className="text-muted-foreground">Belum ada pemandu ditugaskan.</p>
              )}
            </CardContent>
          </Card>
          {isAdmin && (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" className="text-[#DC3545] border-[#DC3545] hover:bg-red-50 bg-white"
                disabled={busy} onClick={doDelete} data-testid="delete-booking-btn">
                <Trash2 className="h-4 w-4 mr-2" /> Padam Tempahan
              </Button>
            </div>
          )}
          {trip && (
            <Card className="border-slate-200 shadow-sm rounded-xl">
              <CardHeader>
                <CardTitle className="text-base font-semibold flex items-center gap-2"><BookOpenText className="h-4 w-4" /> Log Perjalanan</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{trip.distance_km} km · {trip.fuel_liters} L</span>
                  <Badge className="bg-[#10056E] hover:bg-[#10056E]">
                    {trip.kmpl ?? "-"} Km/L
                  </Badge>
                </div>
                <Link to={`/log-perjalanan/${trip.id}`}>
                  <Button variant="outline" size="sm" className="w-full mt-1" data-testid="view-trip-detail-btn">Lihat Log Penuh</Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {tm && (
        <Card className="border-slate-200 shadow-sm rounded-xl" data-testid="booking-telematics-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2"><Satellite className="h-4 w-4" /> Data Telematik Perjalanan</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
              {[
                ["Jarak GPS", `${tm.distance_gps_km} km`],
                ["Purata Kelajuan", `${tm.avg_speed_kmh} km/j`],
                ["Kelajuan Maks", `${tm.max_speed_kmh} km/j`],
                ["Masa Melahu", `${tm.idle_minutes} min`],
                ["Brek Kasar", tm.harsh_braking],
                ["Pecutan Kasar", tm.harsh_acceleration],
                ["Lebihan Laju", tm.overspeed_events],
                ["Jam Enjin", `${tm.engine_hours} j`],
              ].map(([k, val]) => (
                <div key={k} className="bg-slate-50 border rounded-lg p-3">
                  <p className="text-lg font-bold">{val}</p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">{k}</p>
                </div>
              ))}
            </div>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/70">
                    <TableHead>Masa</TableHead><TableHead>Latitud</TableHead><TableHead>Longitud</TableHead>
                    <TableHead>Kelajuan</TableHead><TableHead>Peristiwa</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tm.waypoints.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm font-semibold">{p.time}</TableCell>
                      <TableCell className="text-sm">{p.lat}</TableCell>
                      <TableCell className="text-sm">{p.lng}</TableCell>
                      <TableCell className="text-sm">{p.speed_kmh} km/j</TableCell>
                      <TableCell><Badge variant="secondary" className="text-[10px]">{p.event}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {driverTrips?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="booking-driver-trips">
          <div className="px-6 py-3 border-b flex items-center gap-2">
            <BookOpenText className="h-4 w-4 text-[#10056E]" />
            <p className="text-sm font-semibold">Sejarah Perjalanan Staf — {b.driver_name}</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/70">
                <TableHead>Tarikh</TableHead><TableHead>Kenderaan</TableHead><TableHead>Destinasi</TableHead>
                <TableHead>Jarak (KM)</TableHead><TableHead>Km/L</TableHead><TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {driverTrips.map((t) => (
                <TableRow key={t.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/log-perjalanan/${t.id}`)}>
                  <TableCell className="text-sm">{fmtDate(t.start_datetime)}</TableCell>
                  <TableCell className="text-sm font-semibold">{t.vehicle_reg}</TableCell>
                  <TableCell className="text-sm">{t.destination}</TableCell>
                  <TableCell className="text-sm">{t.distance_km}</TableCell>
                  <TableCell className="text-sm font-semibold">{t.kmpl ?? "-"}</TableCell>
                  <TableCell>
                    <Badge className="bg-[#10056E] hover:bg-[#10056E]">
                      {t.kmpl != null ? `${t.kmpl} Km/L` : "Selesai"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AssignDialog booking={b} open={assignOpen} onOpenChange={setAssignOpen} isAdmin={isAdmin} onDone={load} />

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Tolak Tempahan</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Sebab Penolakan</Label>
              <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="detail-reject-remarks-input" placeholder="Nyatakan sebab..." />
            </div>
            <Button className="w-full bg-[#DC3545] hover:bg-[#bb2d3b]" disabled={busy} onClick={doReject} data-testid="detail-confirm-reject-btn">Tolak Tempahan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
